import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Download, Loader2, Save, Trash2, Upload, UploadCloud, Volume2 } from 'lucide-react';
import { apiService, PackBlockBox, PackBlockDTO, PackGeometryAnalysis, PackGeometryRef, PackTextureRef } from '@/services/api.service';
import { clearPackTextureCache } from '@/services/packAssets';
import { useUnsavedDraft } from '@/stores/unsavedDrafts.store';
import { SearchPicker } from './SearchPicker';
import { RecipeEditor } from './RecipeEditor';
import { DropEditor } from './DropEditor';
import { OversizedDraft, OversizedSettings, toOversizedInput } from './OversizedSettings';
import styles from './PackForm.module.scss';
import { usePackPublishLabels } from './usePackPublishLabels';

type BlockTab = 'info' | 'props' | 'drop' | 'recipe' | 'saison';
const TABS: { id: BlockTab; label: string }[] = [
  { id: 'info', label: 'Information' },
  { id: 'props', label: 'Propriété' },
  { id: 'drop', label: 'Drop' },
  { id: 'recipe', label: 'Recette' },
  { id: 'saison', label: 'Saison' },
];

const RENDER_METHODS = [
  { value: '', label: 'Par défaut' },
  { value: 'opaque', label: 'Solide (opaque)' },
  { value: 'alpha_test', label: 'Découpe (bords transparents)' },
  { value: 'blend', label: 'Transparence lisse' },
];

// tool_type Paladium : 2=pelle, 3=pioche, 4=hache, 1=épée (déduit des blocks existants).
const TOOL_TYPES = [
  { value: '', label: 'Inchangé' },
  { value: '-1', label: 'Aucun (à la main)' },
  { value: '1', label: 'Épée' },
  { value: '2', label: 'Pelle' },
  { value: '3', label: 'Pioche' },
  { value: '4', label: 'Hache' },
];

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
function readAsText(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsText(file);
  });
}

type SoundEntry = { name: string; file: string | null; groupId: string };

// Sons agrégés sur tous les groupes (dont vanilla), mis en cache pour la session.
let soundsCache: Promise<SoundEntry[]> | null = null;
function loadAllSounds(): Promise<SoundEntry[]> {
  if (!soundsCache) {
    soundsCache = (async () => {
      const gr = await apiService.getPacksGroups();
      const groups = gr.data?.groups ?? [];
      const parts = await Promise.all(
        groups.map((g) => apiService.getPackSounds(g.id).then((r) => (r.data?.sounds ?? []).map((s) => ({ name: s.name, file: s.file, groupId: g.id })))),
      );
      const seen = new Set<string>();
      return parts.flat().filter((s) => (seen.has(s.name) ? false : (seen.add(s.name), true)));
    })();
  }
  return soundsCache;
}

interface BlockEditorProps {
  block: PackBlockDTO;
  groupId: string;
  onSaved?: () => void;
  /** Box gérées dans l'aperçu (overlay) ; ici on ne fait que les inclure dans la sauvegarde. */
  collision: PackBlockBox;
  selection: PackBlockBox;
  /** Resource pack en lecture seule (vanilla) : texture/son/noms/import non éditables. */
  readonlyResource?: boolean;
  /** Notifie le parent de l'état d'aperçu (sélection courante + fichiers en attente) pour l'aperçu 3D. */
  onPreviewOverride?: (o: {
    textureUrl: string | null;
    texturePath: string | null;
    geometryText: string | null;
    geometryFile: string | null;
    geometryId: string;
  }) => void;
  /**
   * Gros bloc de déco (groupes avec le drapeau oversizedBlocks seulement) : réglage tenu par le
   * parent, partagé avec l'aperçu 3D (cellules cliquables), envoyé à l'enregistrement.
   */
  oversized?: {
    draft: OversizedDraft;
    setDraft: (d: OversizedDraft) => void;
    analysis: PackGeometryAnalysis | null;
    loading: boolean;
    error: string | null;
  };
}

export function BlockEditor({ block, groupId, onSaved, collision, selection, readonlyResource, onPreviewOverride, oversized }: BlockEditorProps) {
  const [texture, setTexture] = useState(block.texture || '');
  const [renderMethod, setRenderMethod] = useState(block.renderMethod || '');
  const [sound, setSound] = useState(block.sound || '');
  const [geometry, setGeometry] = useState(block.geometry || '');
  const [light, setLight] = useState('');
  const [hardness, setHardness] = useState('');
  const [toolType, setToolType] = useState(''); // '' = inchangé
  const [toolTier, setToolTier] = useState('');
  const [resistance, setResistance] = useState('');
  const [tags, setTags] = useState('');
  /**
   * État du préremplissage depuis le fichier BP brut. Les tags et « ore à XP » ne sont lisibles QUE
   * là : les envoyer alors que cette lecture n'a pas abouti revenait à en demander la suppression,
   * si bien qu'un simple changement de luminosité effaçait les tags du block sans un mot.
   */
  const [compsState, setCompsState] = useState<'loading' | 'ready' | 'error' | 'absent'>('loading');
  const [xpOre, setXpOre] = useState(false);
  const [xpDeepslate, setXpDeepslate] = useState(false);
  const [orientable, setOrientable] = useState(!!block.orientable);
  const [nameEn, setNameEn] = useState('');
  const [nameFr, setNameFr] = useState('');

  const [textures, setTextures] = useState<PackTextureRef[]>([]);
  const [geometries, setGeometries] = useState<PackGeometryRef[]>([]);
  const [sounds, setSounds] = useState<SoundEntry[]>([]);
  // Fichiers en attente : intégrés au pack uniquement à l'enregistrement.
  const [stagedTex, setStagedTex] = useState<File | null>(null);
  const [stagedGeo, setStagedGeo] = useState<File | null>(null);
  const [stagedSound, setStagedSound] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const publishLabels = usePackPublishLabels();
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [tab, setTab] = useState<BlockTab>('info');
  const [dirty, setDirty] = useState(false);
  // Onglets Drop/Recette déjà ouverts : on les garde montés (display:none) pour ne pas
  // perdre leur brouillon en changeant d'onglet.
  const [mountedTabs, setMountedTabs] = useState<Set<BlockTab>>(() => new Set<BlockTab>());
  // Le save principal ne couvre que les onglets info/props (Drop/Recette ont leur propre save).
  const markDirty = () => { if (tab === 'info' || tab === 'props') setDirty(true); };
  // Réglage « gros bloc de déco » modifié (y compris par un clic dans l'aperçu) : à enregistrer.
  const oversizedJson = oversized ? JSON.stringify(oversized.draft) : '';
  const savedOversizedJson = useRef(oversizedJson);
  useEffect(() => {
    if (oversized && oversizedJson !== savedOversizedJson.current) setDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oversizedJson]);

  const texFileRef = useRef<HTMLInputElement>(null);
  const geoFileRef = useRef<HTMLInputElement>(null);
  const soundFileRef = useRef<HTMLInputElement>(null);
  const stagedTexUrl = useMemo(() => (stagedTex ? URL.createObjectURL(stagedTex) : null), [stagedTex]);
  useEffect(() => () => { if (stagedTexUrl) URL.revokeObjectURL(stagedTexUrl); }, [stagedTexUrl]);

  // Reflète l'état courant (sélection + fichiers en attente) dans l'aperçu 3D du parent.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const geometryText = stagedGeo ? await readAsText(stagedGeo) : null;
      if (cancelled) return;
      const texturePath = stagedTex ? null : (textures.find((t) => t.key === texture)?.path ?? null);
      const geometryFile = stagedGeo ? null : (geometries.find((g) => g.id === geometry)?.file ?? null);
      onPreviewOverride?.({ textureUrl: stagedTexUrl, texturePath, geometryText, geometryFile, geometryId: geometry });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedTexUrl, stagedGeo, texture, geometry, textures, geometries]);

  useEffect(() => {
    apiService.getPackTextures(groupId).then((r) => setTextures(r.data?.textures ?? []));
    apiService.getPackGeometries(groupId).then((r) => setGeometries(r.data?.geometries ?? []));
    loadAllSounds().then(setSounds);
    apiService.getPackLang(groupId, `tile.${block.identifier}.name`).then((r) => {
      setNameEn(r.data?.translations?.en_US ?? '');
      setNameFr(r.data?.translations?.fr_FR ?? '');
    });
    // Préremplit luminosité / dureté / résistance depuis le fichier BP.
    if (block.sourceType === 'block' && block.source) {
      setCompsState('loading');
      apiService.getPackFileJson(groupId, block.source).then((r) => {
        // request() ne rejette jamais : en cas d'erreur data est undefined, ce qui donnait un objet
        // vide, donc des champs vides ensuite pris pour des ordres de suppression.
        if (!r.data) { setCompsState('error'); return; }
        const comps = (r.data as any)?.['minecraft:block']?.components ?? {};
        const le = comps['minecraft:light_emission'];
        const lv = typeof le === 'object' && le !== null ? le.emission : le;
        if (lv !== undefined && lv !== null) setLight(String(lv));
        const dm = comps['minecraft:destructible_by_mining'];
        const hv = typeof dm === 'object' ? dm?.hardness ?? dm?.seconds_to_destroy : dm;
        if (hv !== undefined && hv !== null) setHardness(String(hv));
        if (typeof dm === 'object' && dm) {
          if (dm.tool_type !== undefined && dm.tool_type !== null) setToolType(String(dm.tool_type));
          if (dm.tool_tier !== undefined && dm.tool_tier !== null) setToolTier(String(dm.tool_tier));
        }
        const de = comps['minecraft:destructible_by_explosion'];
        // Certains blocks des packs portent la clé vanilla explosion_resistance : sans ce repli le
        // champ restait vide et la sauvegarde ajoutait une seconde clé contradictoire.
        const rv = typeof de === 'object' && de !== null ? (de.value ?? de.explosion_resistance) : de;
        if (rv !== undefined && rv !== null) setResistance(String(rv));
        const tg = comps['minecraft:tags']?.tags;
        if (Array.isArray(tg)) setTags(tg.join(', '));
        const xo = comps['paladium:xp_ore'];
        if (xo) {
          setXpOre(true);
          setXpDeepslate(!!xo.IsDeepslate);
        }
        setCompsState('ready');
      }).catch(() => setCompsState('error'));
    } else {
      // Aucun fichier behavior à lire : ces champs sont inconnus, donc jamais envoyés.
      setCompsState('absent');
    }
  }, [groupId, block.identifier, block.source, block.sourceType]);

  // Télécharge le PNG de la texture actuellement sélectionnée.
  const downloadTexture = async () => {
    const path = textures.find((t) => t.key === texture)?.path;
    if (!path) { setFeedback({ ok: false, msg: 'Sélectionne une texture existante à télécharger.' }); return; }
    const url = await apiService.getPackFileBlobUrl(groupId, path);
    if (!url) { setFeedback({ ok: false, msg: 'Téléchargement impossible (texture introuvable).' }); return; }
    const a = document.createElement('a');
    a.href = url;
    a.download = path.split('/').pop() || `${texture}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const playSound = async () => {
    // Joue le son en attente s'il y en a un, sinon le son déjà défini.
    if (stagedSound) {
      const url = URL.createObjectURL(stagedSound);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      audio.play().catch(() => URL.revokeObjectURL(url));
      return;
    }
    const def = sounds.find((s) => s.name === sound);
    if (!def?.file) {
      setFeedback({ ok: false, msg: 'Aucun fichier audio associé à ce son.' });
      return;
    }
    const url = await apiService.getPackSoundPreviewBlobUrl(def.groupId, def.file);
    if (!url) {
      setFeedback({ ok: false, msg: 'Son non lisible (fichier .fsb non transcodé — vgmstream-cli requis sur le serveur).' });
      return;
    }
    const audio = new Audio(url);
    audio.onended = () => URL.revokeObjectURL(url);
    audio.play().catch(() => URL.revokeObjectURL(url));
  };

  const save = async (): Promise<boolean> => {
    setSaving(true);
    setFeedback(null);

    // 1) Intègre les fichiers en attente (upload) et récupère les clés résultantes.
    let tex = texture;
    let geo = geometry;
    let snd = sound;
    try {
      if (stagedTex) {
        const r = await apiService.importPackTexture(groupId, {
          key: stagedTex.name.replace(/\.[^.]+$/, ''),
          fileName: stagedTex.name,
          fileData: await readAsDataUrl(stagedTex),
          atlas: 'terrain',
        });
        if (!r.data?.success || !r.data.value) { setSaving(false); setFeedback({ ok: false, msg: r.data?.message || r.error || 'Échec import texture' }); return false; }
        tex = r.data.value;
      }
      if (stagedGeo) {
        const r = await apiService.importPackGeometry(groupId, await readAsText(stagedGeo));
        if (!r.data?.success || !r.data.value) { setSaving(false); setFeedback({ ok: false, msg: r.data?.message || r.error || 'Échec import géométrie' }); return false; }
        geo = r.data.value;
      }
      if (stagedSound) {
        const r = await apiService.importPackSound(groupId, { name: stagedSound.name.replace(/\.[^.]+$/, ''), fileData: await readAsDataUrl(stagedSound) });
        if (!r.data?.success || !r.data.value) { setSaving(false); setFeedback({ ok: false, msg: r.data?.message || r.error || 'Échec import son' }); return false; }
        snd = r.data.value;
      }
    } catch {
      setSaving(false);
      setFeedback({ ok: false, msg: "Échec de l'intégration des fichiers" });
      return false;
    }

    // 2) Enregistre le block avec les valeurs (dont les fichiers fraîchement intégrés).
    const res = await apiService.saveBlockForm(groupId, {
      identifier: block.identifier,
      behaviorFile: block.sourceType === 'block' ? block.source : undefined,
      texture: tex,
      renderMethod,
      geometry: geo,
      sound: snd,
      translations: { en_US: nameEn, fr_FR: nameFr },
      light: light === '' ? null : Number(light),
      hardness: hardness === '' ? null : Number(hardness),
      toolType: toolType === '' ? null : Number(toolType),
      toolTier: toolTier === '' ? null : Number(toolTier),
      explosionResistance: resistance === '' ? null : Number(resistance),
      // undefined = « inchangé » côté API. Un tableau vide, lui, ordonne la SUPPRESSION
      // des tags : ne l'envoyer que si l'état réel du fichier a bien été lu.
      tags: compsState === 'ready' ? tags.split(/[,\n]/).map((t) => t.trim()).filter(Boolean) : undefined,
      xpOre: compsState === 'ready' ? xpOre : undefined,
      xpDeepslate: compsState === 'ready' ? xpDeepslate : undefined,
      orientable,
      collisionBox: collision,
      selectionBox: selection,
      // Absent hors drapeau de groupe : le serveur garde alors l'état objet/bloc tel quel.
      oversized: oversized ? toOversizedInput(oversized.draft, oversized.analysis) : undefined,
    });
    setSaving(false);
    if (res.data?.success) {
      savedOversizedJson.current = oversizedJson;
      // Applique les valeurs intégrées, vide le staging et rafraîchit les listes.
      setTexture(tex);
      setGeometry(geo);
      setSound(snd);
      setStagedTex(null);
      setStagedGeo(null);
      setStagedSound(null);
      if (stagedTex) clearPackTextureCache(groupId);
      if (stagedTex) apiService.getPackTextures(groupId).then((r) => setTextures(r.data?.textures ?? []));
      if (stagedGeo) apiService.getPackGeometries(groupId).then((r) => setGeometries(r.data?.geometries ?? []));
      if (stagedSound) { soundsCache = null; loadAllSounds().then(setSounds); }
      setDirty(false);
      setFeedback({ ok: true, msg: res.data.message });
      onSaved?.();
      return true;
    }
    setFeedback({ ok: false, msg: res.data?.message || res.error || 'Échec' });
    return false;
  };

  // Suivi des modifications non enregistrées → barre « Enregistrer tout ».
  useUnsavedDraft({ kind: 'block', groupId, identifier: block.identifier, label: block.identifier, dirty, save });

  const publish = async () => {
    setPublishing(true);
    setFeedback(null);
    const res = await apiService.publishPackGroup(groupId);
    setPublishing(false);
    setFeedback(res.data ? { ok: res.data.success, msg: res.data.messages.join(' · ') } : { ok: false, msg: res.error || 'Échec publication' });
  };

  const texOptions = textures.map((t) => ({ value: t.key, label: t.key, thumbnailPath: t.path }));
  const geoOptions = geometries.map((g) => ({ value: g.id, label: g.id.replace(/^geometry\./, ''), hint: g.id }));

  return (
    <div className={styles.form} onChange={markDirty} onInput={markDirty}>
      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
            onClick={() => {
              setTab(t.id);
              setMountedTabs((s) => (s.has(t.id) ? s : new Set(s).add(t.id)));
              setFeedback(null);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <>
          {readonlyResource && (
            <div className={styles.ok}>Resource pack en lecture seule (vanilla) — seul le <strong>comportement</strong> est éditable.</div>
          )}

          <div className={styles.field}>
            <label className={styles.label}>Identifiant</label>
            <input className={styles.input} value={block.identifier} readOnly disabled />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Texture</label>
            {readonlyResource ? (
              <div className={styles.pickerRow}>
                <input className={`${styles.input} ${styles.pickerGrow}`} value={texture} readOnly disabled />
                <button type="button" className={styles.uploadBtn} onClick={downloadTexture} title="Télécharger la texture">
                  <Download size={14} /> Télécharger
                </button>
              </div>
            ) : stagedTex ? (
              <div className={styles.stagedFile}>
                {stagedTexUrl && <img className={styles.stagedThumb} src={stagedTexUrl} alt="" />}
                <span className={styles.stagedName}>{stagedTex.name}</span>
                <span className={styles.stagedTag}>à intégrer</span>
                <button type="button" className={styles.stagedDel} onClick={() => setStagedTex(null)} title="Supprimer">
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <div className={styles.pickerRow}>
                <div className={styles.pickerGrow}>
                  <SearchPicker groupId={groupId} value={texture} onChange={(v) => { setTexture(v); markDirty(); }} options={texOptions} showThumbnails placeholder="Clé de texture…" />
                </div>
                <button type="button" className={styles.uploadBtn} onClick={downloadTexture} disabled={!texture} title="Télécharger la texture sélectionnée">
                  <Download size={14} />
                </button>
                <button className={styles.uploadBtn} onClick={() => texFileRef.current?.click()}>
                  <Upload size={14} /> Importer
                  <input ref={texFileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && setStagedTex(e.target.files[0])} />
                </button>
              </div>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Géométrie</label>
            {stagedGeo ? (
              <div className={styles.stagedFile}>
                <span className={styles.stagedName}>{stagedGeo.name}</span>
                <span className={styles.stagedTag}>à intégrer</span>
                <button type="button" className={styles.stagedDel} onClick={() => setStagedGeo(null)} title="Supprimer">
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <div className={styles.pickerRow}>
                <div className={styles.pickerGrow}>
                  <SearchPicker groupId={groupId} value={geometry} onChange={(v) => { setGeometry(v); markDirty(); }} options={geoOptions} placeholder="geometry.… (vide = cube plein)" />
                </div>
                <button className={styles.uploadBtn} disabled={readonlyResource} onClick={() => geoFileRef.current?.click()}>
                  <Upload size={14} /> Importer
                  <input ref={geoFileRef} type="file" accept=".json,.geo.json,application/json" onChange={(e) => e.target.files?.[0] && setStagedGeo(e.target.files[0])} />
                </button>
              </div>
            )}
          </div>

          {oversized && (
            <div className={styles.field}>
              <OversizedSettings
                block={block}
                draft={oversized.draft}
                onChange={oversized.setDraft}
                analysis={oversized.analysis}
                loading={oversized.loading}
                error={oversized.error}
                geometry={stagedGeo ? stagedGeo.name : geometry}
                disabled={readonlyResource}
              />
            </div>
          )}

          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Nom affiché (EN)</label>
              <input className={styles.input} value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="Crusher" disabled={readonlyResource} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Nom affiché (FR)</label>
              <input className={styles.input} value={nameFr} onChange={(e) => setNameFr(e.target.value)} placeholder="Broyeur" disabled={readonlyResource} />
            </div>
          </div>
        </>
      )}

      {tab === 'props' && (
        <>
          <div className={styles.field}>
            <label className={styles.label}>Méthode de rendu</label>
            <select className={styles.select} value={renderMethod} onChange={(e) => setRenderMethod(e.target.value)}>
              {RENDER_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Son</label>
            {stagedSound ? (
              <div className={styles.stagedFile}>
                <span className={styles.stagedName}>{stagedSound.name}</span>
                <span className={styles.stagedTag}>à intégrer</span>
                <button type="button" className={styles.uploadBtn} onClick={playSound} title="Écouter">
                  <Volume2 size={14} /> Écouter
                </button>
                <button type="button" className={styles.stagedDel} onClick={() => setStagedSound(null)} title="Supprimer">
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <div className={styles.pickerRow}>
                <input className={`${styles.input} ${styles.pickerGrow}`} list="block-sounds" value={sound} onChange={(e) => setSound(e.target.value)} placeholder="stone, wood, mon_son…" disabled={readonlyResource} />
                <button type="button" className={styles.uploadBtn} onClick={playSound} title="Écouter le son">
                  <Volume2 size={14} /> Écouter
                </button>
                {!readonlyResource && (
                  <button className={styles.uploadBtn} onClick={() => soundFileRef.current?.click()}>
                    <Upload size={14} /> .ogg
                    <input ref={soundFileRef} type="file" accept=".ogg,audio/ogg" onChange={(e) => e.target.files?.[0] && setStagedSound(e.target.files[0])} />
                  </button>
                )}
              </div>
            )}
            <datalist id="block-sounds">
              {sounds.filter((s) => s.file).map((s) => <option key={s.name} value={s.name} />)}
            </datalist>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              Luminosité <span className={styles.sliderValue}>{light === '' ? 0 : light} / 15</span>
            </label>
            <input className={styles.slider} type="range" min={0} max={15} step={1} value={light === '' ? 0 : Number(light)} onChange={(e) => setLight(e.target.value)} />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Dureté (résistance au minage)</label>
            <input className={styles.input} type="number" min={0} step={0.5} value={hardness} onChange={(e) => setHardness(e.target.value)} placeholder="ex. 3" />
          </div>

          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Outil requis</label>
              <select className={styles.select} value={toolType} onChange={(e) => setToolType(e.target.value)}>
                {TOOL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Palier d'outil (0=bois … 4=diamant, +)</label>
              <input className={styles.input} type="number" min={0} value={toolTier} onChange={(e) => setToolTier(e.target.value)} placeholder="inchangé" />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Résistance aux explosions</label>
            <input className={styles.input} type="number" min={0} value={resistance} onChange={(e) => setResistance(e.target.value)} placeholder="inchangé" />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Tags (séparés par des virgules)</label>
            <input className={styles.input} value={tags} onChange={(e) => setTags(e.target.value)}
              disabled={compsState !== 'ready'}
              placeholder={compsState === 'ready' ? 'ex. paladium:elevator, minecraft:stone' : 'non chargé'} />
            {compsState === 'error' && (
              <span className={styles.hint}>
                Lecture du fichier behavior impossible : tags et « ore à XP » ne sont pas chargés, ils
                seront laissés tels quels à l'enregistrement. Rafraîchis la page pour réessayer.
              </span>
            )}
          </div>

          <label className={styles.checkRow}>
            <input type="checkbox" checked={orientable} onChange={(e) => setOrientable(e.target.checked)} />
            <span>Orientable — s'oriente selon la direction du joueur à la pose</span>
          </label>

          <label className={styles.checkRow}>
            <input type="checkbox" checked={xpOre} disabled={compsState !== 'ready'}
              onChange={(e) => setXpOre(e.target.checked)} />
            <span>Ore à XP — lâche de l'XP au minage (paladium:xp_ore, quantité gérée par le moteur)</span>
          </label>
          {xpOre && (
            <label className={styles.checkRow} style={{ marginLeft: 24 }}>
              <input type="checkbox" checked={xpDeepslate} onChange={(e) => setXpDeepslate(e.target.checked)} />
              <span>Variante deepslate</span>
            </label>
          )}
        </>
      )}

      {mountedTabs.has('drop') && (
        <div style={tab === 'drop' ? undefined : { display: 'none' }}>
          <DropEditor block={block} groupId={groupId} onSaved={onSaved} />
        </div>
      )}

      {mountedTabs.has('recipe') && (
        <div style={tab === 'recipe' ? undefined : { display: 'none' }}>
          <RecipeEditor identifier={block.identifier} thumbnail={block.thumbnail} groupId={groupId} draftKind="block" draftScope="recipe" onSaved={onSaved} />
        </div>
      )}

      {tab === 'saison' && <div className={styles.tabPlaceholder}>Gestion des saisons — à définir.</div>}

      {(tab === 'info' || tab === 'props') && (
        <>
          {feedback && (
            <div className={feedback.ok ? styles.ok : styles.error}>
              {feedback.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {feedback.msg}
            </div>
          )}
          <div className={styles.actions}>
            <button className={styles.saveBtn} onClick={save} disabled={saving}>
              {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />}
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button className={styles.publishBtn} onClick={publish} disabled={publishing} title={publishLabels.title}>
              {publishing ? <Loader2 size={14} className={styles.spin} /> : <UploadCloud size={14} />}
              {publishing ? publishLabels.busy : publishLabels.idle}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
